import type { UserRole } from "@/contexts/AuthContext";

/**
 * Which paths the root gate treats as public, as nowhere, as nothing anybody
 * asked for, and as out of a reader's reach.
 *
 * **Here rather than in the gate, because all of it decides where a reader ends
 * up and none of it is reachable from there.** The gate is an effect inside a
 * route component, so exercising these decisions in place means standing up a
 * router, an auth context and a resolved profile — which is how a trailing
 * slash came to dead-end the sign-in page with nothing to catch it.
 */

const PUBLIC_PATHS = new Set<string>([
  "/login",
  "/login-link",
  "/login-link-modal",
]);

export function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATHS.has(pathname);
}

/** Where a signed-in account with no role goes (NAV-101). Not a public path:
 *  it is only ever reached signed in, and a signed-out visitor there is sent
 *  to /login like anywhere else. */
export const NO_ACCESS_PATH = "/no-access";

/** Landings nobody is *sent* to — where the app puts somebody who asked for
 *  nothing in particular. `/home` and `/end-user` are redirectors rather than
 *  pages (both end at a role dashboard), which is why the dashboards
 *  themselves are matched by suffix below rather than listed here.
 *
 *  **Not `/end-user/tools`**, though the walkthrough lands there: it is a page
 *  people share *with* filters, which are the whole content of such a link.
 *  Nothing is lost by capturing it — the walkthrough passes it to
 *  `restoreOrHome` as the fallback anyway. */
const GENERIC_LANDINGS = new Set<string>([
  "/",
  "/home",
  "/onboarding",
  // **All four area front doors.** Each of
  // `routes/{end-user,admin,bu-admin,group-legal}/index.tsx` is an index
  // redirector to that area's dashboard, so carrying one is carrying the
  // dashboard under another name.
  "/end-user",
  "/admin",
  "/bu-admin",
  "/group-legal",
]);

/**
 * One pathname, in the shape every comparison in the gate expects: no trailing
 * slash, and lower case.
 *
 * **The router's `trailingSlash` option does not do this**, and it is worth
 * being precise about why, because the option exists and reaching for it here
 * would look right. It defaults to `'never'` and is threaded through
 * `resolvePath`, so it governs the paths the router *builds* — every `Link`,
 * every `navigate`. What it never touches is an inbound `location.pathname`,
 * which is whatever the address bar, an emailed link or a proxy handed us. That
 * is the value the gate branches on.
 *
 * **Lower case because the router matches that way.** `router-core` compares a
 * static route segment with `.toLowerCase()` on both sides unless the route
 * sets `caseSensitive`, and no route here does — so `/BU-ADMIN/requests/req-1`
 * reaches the `/bu-admin` guard and is refused, while a case-sensitive scan
 * over the same string finds no area at all and calls it a page anybody may
 * have. Every reader of this value compares it against a lower-case literal;
 * nothing navigates with it, so an id keeps the case it was given.
 *
 * `//` collapsing to the empty string is the other case worth the guard: `""`
 * matches none of the tests below, so it read as an errand and went into
 * `?redirect=`.
 */
export function normalisePath(pathname: string): string {
  const stripped = pathname.replace(/\/+$/, "").toLowerCase();
  // One guard, not two: an early return for a single-character path looks like
  // it protects the root, and it is redundant — `"/"` strips to `""` and this
  // line puts it back.
  return stripped === "" ? "/" : stripped;
}

/**
 * Is this page something the reader was actually trying to reach?
 *
 * An interruption — no role yet, onboarding unfinished, not signed in — has to
 * carry the errand along, or the detour is where a deep link dies. Two things
 * can supply it: `?redirect=`, written when the signed-out branch bounced them
 * through /login, and on a cold load with a persisted session the page they are
 * simply standing on. The second is right for a deep link and wrong for a
 * landing, and carrying a landing is not harmless: it turns "no errand" into an
 * errand and quietly overrules the destination the walkthrough chooses on
 * purpose (the catalogue, not a dashboard).
 *
 * Derived rather than hand-listed, because a hand-list gets the *redirectors*
 * and misses what they redirect to — `/home` renders `<Navigate>` to
 * `/end-user/dashboard` as soon as `role` is set, so the dashboard is what the
 * gate actually sees whenever that commit lands first.
 */
export function isNotAnErrand(pathname: string): boolean {
  const path = normalisePath(pathname);
  return (
    isPublicPath(path) ||
    path === NO_ACCESS_PATH ||
    path.endsWith("/dashboard") ||
    GENERIC_LANDINGS.has(path)
  );
}

/**
 * Where the walkthrough lands somebody who asked for nothing in particular.
 *
 * Stated here rather than only in the root gate, because the last screen's copy
 * has to agree with it: promising "back to what you came here for" and then
 * landing on the catalogue front page is a small lie the very next screen
 * exposes.
 */
export const WALKTHROUGH_LANDING = "/end-user/tools";

/**
 * Which roles each area admits.
 *
 * **One list, read by both the guard that enforces it and the copy that
 * promises where Finish goes.** `routes/{admin,bu-admin,group-legal,end-user}/route.tsx`
 * pass these to `requireRole` rather than their own literals, because a second
 * copy of this is a promise that can disagree with the routing — which is
 * exactly the defect the two predicates below were added to fix, one level up.
 *
 * Keyed by each area's own route path, which is the prefix an errand carries.
 */
export const AREA_ROLES = {
  "/admin": ["super_admin"],
  "/bu-admin": ["bu_admin", "super_admin"],
  "/group-legal": ["group_legal", "super_admin"],
  // Every role is also an end user (source-app semantics; the portal switcher
  // in the user dropdown relies on it) — docs/adr/0001-end-user-url-structure.md.
  "/end-user": ["end_user", "bu_admin", "super_admin", "group_legal"],
} as const satisfies Record<string, readonly UserRole[]>;

type Area = keyof typeof AREA_ROLES;

/**
 * Can this reader actually get to the page a path names?
 *
 * A path in no area at all — `/home`, `/no-access`, something unrecognised —
 * answers yes: the four areas are the only role-gated prefixes, and a path
 * outside them is left to whatever guards it has of its own. Answering no
 * there would refuse readers on the strength of not knowing.
 */
export function roleCanReach(
  pathname: string,
  role: UserRole | null | undefined,
): boolean {
  const path = normalisePath(pathname);
  const area = (Object.keys(AREA_ROLES) as Area[]).find(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  );
  if (!area) return true;
  // Widened deliberately: `as const` narrows each entry to its own literals, so
  // `includes` would reject a `UserRole` for not being one of *those* roles —
  // which is the question being asked, not a type error.
  const admitted: readonly UserRole[] = AREA_ROLES[area];
  return role !== null && role !== undefined && admitted.includes(role);
}

/**
 * The two things the root gate reads out of a location's search.
 *
 * **Tested for, not asserted.** `location.search` on the router's own snapshot
 * is parsed before any route's `validateSearch` runs, so the values are
 * whatever JSON made of them: `?redirect=5` is the number 5 and
 * `?redirect={"a":1}` an object. Casting that to a string handed a number to
 * the predicates below, which read it as a path and sent the reader to `/5`.
 *
 * The gate reads the snapshot rather than the validated search because it
 * branches on the path and the search together, and those are two
 * subscriptions that disagree for one render on every client-side navigation.
 * Narrowing here is what makes that safe.
 */
export function readGateSearch(search: unknown): {
  redirect: string | undefined;
  replay: boolean;
} {
  if (typeof search !== "object" || search === null) {
    return { redirect: undefined, replay: false };
  }
  const raw = search as Record<string, unknown>;
  return {
    redirect: typeof raw.redirect === "string" ? raw.redirect : undefined,
    replay: raw.replay === true,
  };
}

/**
 * Is a captured errand one the *walkthrough* should honour?
 *
 * `requireRole` captures every href it bounces, unconditionally and rightly: it
 * runs signed out, so it cannot know whether the reader is a new joiner or an
 * admin returning to a bookmark, and three other consumers need the exact
 * destination back. Only the walkthrough wants a landing dropped, because only
 * there does "the reader chose nothing" have a better answer than the address
 * bar — so that question is asked here rather than at the capture.
 *
 * Three ways to be not worth keeping, and each of them is a destination this
 * cannot deliver:
 *
 * - **A landing.** Following it overrules the walkthrough's own choice with the
 *   page the reader happened to be standing on.
 * - **A page their role cannot enter.** An area guard bounces them to `/home`,
 *   i.e. their dashboard — the one destination the walkthrough deliberately
 *   does not end on.
 * - **Anything that will not parse.** `restoreOrHome` cannot build a
 *   navigation from it and falls back.
 */
export function errandIsWorthKeeping(
  redirect: string | undefined,
  role: UserRole | null | undefined,
): boolean {
  if (!redirect) return false;
  try {
    const { pathname } = new URL(redirect, "http://localhost");
    return !isNotAnErrand(pathname) && roleCanReach(pathname, role);
  } catch {
    return false;
  }
}

/**
 * Does finishing take this reader somewhere more specific than the page the
 * walkthrough would have picked for them?
 *
 * **The question the last screen's copy needs**, which is not "is there an
 * errand": a new colleague who happened to be standing on the catalogue when
 * the gate fired carries it as one — correctly, since it is a page people share
 * *with filters* — and promising them "back to what you came here for" when
 * they chose nothing and are going where everybody else goes is the same
 * destination under an odd name.
 *
 * **Defined in terms of `errandIsWorthKeeping` rather than beside it**, because
 * the two answers are about one journey: the copy names where Finish goes, so
 * an errand the walkthrough will not honour cannot be specific however precise
 * it looks. Asked separately, they disagreed about every landing — the closing
 * line promised `/end-user/dashboard` and the reader arrived at the catalogue,
 * which is the contradiction this pair exists to prevent (PRD I6). It is also
 * why the search/hash test sits *after* the honouring question and not in front
 * of it: `/home?x=1` is a landing whatever it carries.
 *
 * What remains here is the one distinction the honouring question does not
 * make: a filtered catalogue link (`/end-user/tools?category=Design`) is
 * something specific, while the bare catalogue is the fallback under another
 * name.
 */
export function errandGoesSomewhereSpecific(
  redirect: string | undefined,
  role: UserRole | null | undefined,
): boolean {
  if (!redirect || !errandIsWorthKeeping(redirect, role)) return false;
  // Cannot throw: `errandIsWorthKeeping` just parsed this same value with this
  // same base and answered true.
  const url = new URL(redirect, "http://localhost");
  if (url.search !== "" || url.hash !== "") return true;
  return normalisePath(url.pathname) !== WALKTHROUGH_LANDING;
}

/** Where a reader with no errand goes when they are not an employee: their own
 *  area's front door, which forwards to that area's dashboard. */
const HOME_PATH = "/home";

/** What the closing line may say, because it is what will happen. */
export type FinishKind = "errand" | "catalogue" | "dashboard";

/**
 * Where finishing the walkthrough takes this reader.
 *
 * **One answer, read by the gate that navigates and by the line that promises.**
 * The two were separate: the gate sent anybody who is not an employee to their
 * own dashboard, while the line asked only whether the errand was specific and
 * said "next: the catalogue" for everybody else. Admitting admins to the
 * walkthrough made that wrong on the ordinary path — three of the four roles
 * were told the catalogue on their first morning and landed on a dashboard,
 * which is the contradiction this pair exists to prevent (PRD I6).
 *
 * `kind` is derived from the destination rather than chosen beside it, so a new
 * landing cannot acquire a sentence that disagrees with it.
 */
export function finishLanding(
  redirect: string | undefined,
  role: UserRole | null | undefined,
): { fallback: string; honourErrand: boolean; kind: FinishKind } {
  const fallback = role === "end_user" ? WALKTHROUGH_LANDING : HOME_PATH;
  const honourErrand = errandIsWorthKeeping(redirect, role);
  if (honourErrand && errandGoesSomewhereSpecific(redirect, role)) {
    return { fallback, honourErrand, kind: "errand" };
  }
  // Not specific, so the destination is either the fallback or an errand that
  // resolves to the same page the fallback would have. The catalogue is the
  // one page the walkthrough picks for itself; anything else is a dashboard.
  const landing = honourErrand
    ? normalisePath(new URL(redirect ?? "", "http://localhost").pathname)
    : fallback;
  return {
    fallback,
    honourErrand,
    kind: landing === WALKTHROUGH_LANDING ? "catalogue" : "dashboard",
  };
}
